import type {
  ActorException,
  ActorWithRoles,
  ApiKeyUsageView,
  AssignMemberBody,
  CreateRoleBody,
  ExceptionBody,
  PermissionRow,
  ResourceView,
  RoleDetail,
  RoleView,
  ServiceAccountView,
  UpsertExceptionBody,
} from '@/api/(admin)/permissions/types';
import type { PermissionBlueprint } from '@/api/permissions/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * IAM management, scoped to a tenant the operator picked rather than to their
 * own company.
 *
 * These are the same routes `@/api/permissions` calls for the customer app. The
 * only difference is `x-retraced-tenant-id`: `tenantOf` on the backend prefers
 * that header and falls back to the caller's company context, so passing it is
 * what makes an operator able to administer someone else's tenant. Every
 * function here therefore takes the tenant explicitly — there is no ambient
 * default, and a call that forgot one would silently manage Retraced's own
 * company.
 */

const ROOT = '/api/permissions';
const TENANT_HEADER = 'x-retraced-tenant-id';

function scoped(tenantId: string, init: RequestInit = {}): RequestInit {
  return { ...init, headers: { ...init.headers, [TENANT_HEADER]: tenantId } };
}

/* ── catalog ─────────────────────────────────────────────────────────────── */

/** Resource types, without their actions — pair with `fetchCatalog`. */
export const fetchResources = (tenantId: string) =>
  apiFetch<{ data: ResourceView[] }>(
    `${ROOT}/resources`,
    scoped(tenantId),
    API_ROOT_URL,
  );

/** Every `{resource}:{action}` row, including actions a resource added itself. */
export const fetchCatalog = (tenantId: string) =>
  apiFetch<{ data: PermissionRow[] }>(
    `${ROOT}/catalog`,
    scoped(tenantId),
    API_ROOT_URL,
  );

/**
 * The authoring blueprint: feature groups, their resources, the actions each
 * supports, and the reusable templates. One payload drives the whole permission
 * editor — `/catalog` is the flat row list behind it and is only used where a
 * plain `resource:action` list is wanted.
 */
export const fetchBlueprint = (tenantId: string) =>
  apiFetch<{ data: PermissionBlueprint }>(
    `${ROOT}/catalog/blueprint`,
    scoped(tenantId),
    API_ROOT_URL,
  );

/* ── roles ───────────────────────────────────────────────────────────────── */

export const fetchRoles = (tenantId: string) =>
  apiFetch<{ data: RoleView[] }>(
    `${ROOT}/roles`,
    scoped(tenantId),
    API_ROOT_URL,
  );

export const fetchRole = (tenantId: string, key: string) =>
  apiFetch<{ data: RoleDetail }>(
    `${ROOT}/roles/${encodeURIComponent(key)}`,
    scoped(tenantId),
    API_ROOT_URL,
  );

export const createRole = (tenantId: string, body: CreateRoleBody) =>
  apiFetch<{ data: RoleView }>(
    `${ROOT}/roles`,
    scoped(tenantId, { method: 'POST', body: JSON.stringify(body) }),
    API_ROOT_URL,
  );

/** Rejected with 403 for a system role — the backend checks before deleting. */
export const deleteRole = (tenantId: string, key: string) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}`,
    scoped(tenantId, { method: 'DELETE' }),
    API_ROOT_URL,
  );

export const grantPermission = (
  tenantId: string,
  key: string,
  permission: string,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/permissions`,
    scoped(tenantId, {
      method: 'POST',
      body: JSON.stringify({ permission }),
    }),
    API_ROOT_URL,
  );

/** A DELETE with a body — the permission travels in it, not the path. */
export const revokePermission = (
  tenantId: string,
  key: string,
  permission: string,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/permissions`,
    scoped(tenantId, {
      method: 'DELETE',
      body: JSON.stringify({ permission }),
    }),
    API_ROOT_URL,
  );

/**
 * Replaces the role's whole permission set in one call.
 *
 * The editor is a draft the operator saves, not a live toggle, so the write is
 * the set it ended on — the same route and semantics the customer app's
 * permission-set wizard uses. The per-permission grant/revoke routes remain for
 * anything that really does write one at a time.
 */
export const setRolePermissions = (
  tenantId: string,
  key: string,
  permissions: string[],
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/permissions`,
    scoped(tenantId, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),
    API_ROOT_URL,
  );

export const assignRoleMember = (
  tenantId: string,
  key: string,
  body: AssignMemberBody,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/members`,
    scoped(tenantId, { method: 'POST', body: JSON.stringify(body) }),
    API_ROOT_URL,
  );

export const removeRoleMember = (
  tenantId: string,
  key: string,
  actorType: string,
  actorId: string,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/members/${encodeURIComponent(
      actorType,
    )}/${encodeURIComponent(actorId)}`,
    scoped(tenantId, { method: 'DELETE' }),
    API_ROOT_URL,
  );

/* ── directory ───────────────────────────────────────────────────────────── */

export const fetchActors = (tenantId: string) =>
  apiFetch<{ data: ActorWithRoles[] }>(
    `${ROOT}/actors`,
    scoped(tenantId),
    API_ROOT_URL,
  );

export const fetchServiceAccounts = (tenantId: string) =>
  apiFetch<{ data: ServiceAccountView[] }>(
    `${ROOT}/service-accounts`,
    scoped(tenantId),
    API_ROOT_URL,
  );

export const createServiceAccount = (tenantId: string, name: string) =>
  apiFetch<{ data: ServiceAccountView }>(
    `${ROOT}/service-accounts`,
    scoped(tenantId, { method: 'POST', body: JSON.stringify({ name }) }),
    API_ROOT_URL,
  );

/** `date` is a UTC `YYYY-MM-DD`; the counts are that day's rollup. */
export const fetchApiKeyUsage = (tenantId: string, date: string) =>
  apiFetch<{ data: ApiKeyUsageView[] }>(
    `${ROOT}/service-accounts/api-key-usage?date=${encodeURIComponent(date)}`,
    scoped(tenantId),
    API_ROOT_URL,
  );

/* ── per-record exceptions ───────────────────────────────────────────────── */

/**
 * Share (allow) or block (deny) one record for one user.
 *
 * Deny wins over both a role grant and a share, and the backend answers 409
 * when a denied record is shared — the caller surfaces that message rather
 * than pretending the write landed.
 */
export const upsertException = (tenantId: string, body: UpsertExceptionBody) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/exceptions`,
    scoped(tenantId, { method: 'POST', body: JSON.stringify(body) }),
    API_ROOT_URL,
  );

export const clearException = (tenantId: string, body: ExceptionBody) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/exceptions`,
    scoped(tenantId, { method: 'DELETE', body: JSON.stringify(body) }),
    API_ROOT_URL,
  );

export const fetchActorExceptions = (tenantId: string, userId: string) =>
  apiFetch<{ data: ActorException[] }>(
    `${ROOT}/users/${encodeURIComponent(userId)}/exceptions`,
    scoped(tenantId),
    API_ROOT_URL,
  );
